/**
 * Diagnostics tool (Blueprint v2 P9): run the project's typecheck / lint / test /
 * build in the workspace and capture STRUCTURED results (pass/fail + the failing
 * files parsed from the output), so the repair loop can feed back only the failing
 * file + exact error rather than a wall of text.
 */
import { spawn, spawnSync } from 'node:child_process';
import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';

export interface Check {
  readonly name: string;
  readonly command: string;
}

export interface CheckResult {
  readonly name: string;
  readonly command: string;
  readonly ok: boolean;
  readonly output: string;
  /** Best-effort list of files implicated by the output. */
  readonly failingFiles: string[];
  readonly durationMs: number;
}

interface PackageJson {
  scripts?: Record<string, string>;
}

function readPackageJson(root: string): PackageJson | null {
  try {
    return JSON.parse(readFileSync(join(root, 'package.json'), 'utf8')) as PackageJson;
  } catch {
    return null;
  }
}

function detectPm(root: string): string {
  if (existsSync(join(root, 'pnpm-lock.yaml'))) return 'pnpm';
  if (existsSync(join(root, 'yarn.lock'))) return 'yarn';
  return 'npm';
}

/** The check scripts we know how to run, in repair-priority order. */
const KNOWN_CHECKS = ['typecheck', 'lint', 'test', 'build'] as const;

/** Detect runnable checks from the repo's package.json scripts. */
export function detectChecks(root: string): Check[] {
  const scripts = readPackageJson(root)?.scripts ?? {};
  const pm = detectPm(root);
  const out: Check[] = [];
  for (const name of KNOWN_CHECKS) {
    if (typeof scripts[name] === 'string') out.push({ name, command: `${pm} run ${name}` });
  }
  return out;
}

/** Parse plausible source-file paths out of compiler/linter output. */
function parseFailingFiles(output: string): string[] {
  const files = new Set<string>();
  // `path/to/file.ts(12,3)` (tsc) and `path/to/file.ts:12:3` (eslint/jest).
  const re = /([A-Za-z0-9_./\\-]+\.[A-Za-z]{1,5})[(:]\d+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(output)) !== null) {
    const p = m[1];
    if (p && !p.includes('node_modules')) files.add(p.split('\\').join('/'));
    if (files.size >= 50) break;
  }
  return [...files];
}

/** Run one check in `root`, capturing combined output (best-effort, never throws). */
export function runCheck(root: string, check: Check, timeoutMs = 300_000): CheckResult {
  let output = '';
  let ok = false;
  let durationMs = 0;
  try {
    const startedHint = process.hrtime.bigint();
    const res = spawnSync(check.command, {
      cwd: root,
      shell: true,
      encoding: 'utf8',
      timeout: timeoutMs,
      maxBuffer: 16 * 1024 * 1024,
    });
    durationMs = Number((process.hrtime.bigint() - startedHint) / 1_000_000n);
    output = `${res.stdout ?? ''}${res.stderr ?? ''}`;
    ok = res.status === 0 && !res.error;
  } catch (err) {
    output = err instanceof Error ? err.message : String(err);
    ok = false;
  }
  return { name: check.name, command: check.command, ok, output, failingFiles: parseFailingFiles(output), durationMs };
}

/** Run a set of checks (or the detected ones) and return structured results. */
export function runDiagnostics(root: string, checks?: readonly Check[]): CheckResult[] {
  const toRun = checks ?? detectChecks(root);
  return toRun.map((c) => runCheck(root, c));
}

/** A check result that also distinguishes "killed at the time budget". */
export interface AsyncCheckResult extends CheckResult {
  /** True when the check was killed by the timeout/abort, so `ok:false` is inconclusive. */
  readonly timedOut?: boolean;
}

/**
 * Kill a `shell: true` child AND its process tree. A plain `child.kill()` only
 * kills the shell — on Windows the actual tool (the grandchild) keeps running,
 * keeps the stdio pipes open, and `close` never fires.
 */
function killTree(pid: number | undefined): void {
  if (!pid) return;
  try {
    if (process.platform === 'win32') {
      spawn('taskkill', ['/pid', String(pid), '/T', '/F'], { stdio: 'ignore' }).unref();
    } else {
      process.kill(pid, 'SIGKILL');
    }
  } catch {
    /* already gone */
  }
}

/**
 * Non-blocking variant of {@link runCheck}: spawns the command so the event
 * loop (spinner, Ctrl+C, queued input) stays alive while a slow typecheck or
 * test run executes. Aborting the signal (or the time budget) kills the whole
 * process tree; if stubborn grandchildren still hold the pipes open, a short
 * fallback timer settles the result anyway.
 */
export function runCheckAsync(
  root: string,
  check: Check,
  opts: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<AsyncCheckResult> {
  const timeoutMs = opts.timeoutMs ?? 300_000;
  return new Promise((resolve) => {
    const started = process.hrtime.bigint();
    let output = '';
    let killed = false;
    let settled = false;
    let fallback: ReturnType<typeof setTimeout> | null = null;
    const child = spawn(check.command, { cwd: root, shell: true });
    const finish = (ok: boolean): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (fallback) clearTimeout(fallback);
      opts.signal?.removeEventListener('abort', onAbort);
      child.stdout?.destroy();
      child.stderr?.destroy();
      child.unref();
      const durationMs = Number((process.hrtime.bigint() - started) / 1_000_000n);
      resolve({
        name: check.name,
        command: check.command,
        ok,
        output: output.slice(0, 16 * 1024 * 1024),
        failingFiles: parseFailingFiles(output),
        durationMs,
        ...(killed ? { timedOut: true } : {}),
      });
    };
    const kill = (): void => {
      killed = true;
      killTree(child.pid);
      // Don't wait on `close` forever if a grandchild survives the kill.
      fallback = setTimeout(() => finish(false), 2000);
      fallback.unref?.();
    };
    const timer = setTimeout(kill, timeoutMs);
    const onAbort = (): void => kill();
    opts.signal?.addEventListener('abort', onAbort, { once: true });
    child.stdout?.on('data', (d: Buffer) => {
      output += d.toString('utf8');
    });
    child.stderr?.on('data', (d: Buffer) => {
      output += d.toString('utf8');
    });
    child.on('error', (err) => {
      output += err.message;
      finish(false);
    });
    child.on('close', (code) => finish(code === 0 && !killed));
  });
}

/**
 * Run checks sequentially without blocking the event loop. With `failFast`,
 * stop at the first real failure (timeouts don't trip it) so the repair loop
 * gets feedback as early as possible.
 */
export async function runDiagnosticsAsync(
  root: string,
  opts: { checks?: readonly Check[]; timeoutMs?: number; signal?: AbortSignal; failFast?: boolean } = {},
): Promise<AsyncCheckResult[]> {
  const toRun = opts.checks ?? detectChecks(root);
  const results: AsyncCheckResult[] = [];
  for (const check of toRun) {
    if (opts.signal?.aborted) break;
    const r = await runCheckAsync(root, check, {
      ...(opts.timeoutMs !== undefined ? { timeoutMs: opts.timeoutMs } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
    results.push(r);
    if (opts.failFast && !r.ok && !r.timedOut) break;
  }
  return results;
}
